import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { GenericApplicationStepValue } from '@/components/modules/Application/types/GenericApplicationStep';
import { developOptions } from '@/config/developOptions';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useUser } from '@/hooks/useUser';

export const useApplicationNavigation = () => {
  const { userEntity, agent, isUserAgent } = useUser();
  const {
    state: { step, steps, showMobileSummary, isDirty, isCompleted },
    actions: { updateApplicationContext, resetApplicationContext },
  } = useApplicationContext();

  const initApplication = () => {
    if (!userEntity) return [] as GenericApplicationStepValue[];

    resetApplicationContext();

    updateApplicationContext({
      selectedMerchant: userEntity.merchant,
    });

    if (isUserAgent && agent) {
      updateApplicationContext({
        selectedAgent: agent,
        selectedAgentHasError: false,
        checkoutSessionId: uuidv4(),
        cart: CartEntity.initial(agent.allowedPurchaseCountries[0]),
        ...(developOptions.USE_FAKE_FORMS_AS_DEFAULT_VALUES && {
          orderReference1: uuidv4().split('-')[4],
        }),
      });
    }
  };

  const isPrevEnabled = useMemo(() => step && steps && steps.indexOf(step) > 0, [step, steps]);
  const isNextEnabled = useMemo(() => step && steps && steps.indexOf(step) < steps.length - 1, [step, steps]);

  const onPrev =
    (isPrevEnabled &&
      (() => {
        if (!step) return;
        const prevStep = steps[steps.indexOf(step) - 1];
        if (!prevStep) return;
        setStep(prevStep);
      })) ||
    undefined;

  const onNext =
    (isNextEnabled &&
      (() => {
        if (!step) return;
        const nextStep = steps[steps.indexOf(step) + 1];
        if (!nextStep) return;
        setStep(nextStep);
      })) ||
    (() => {});

  const resetNavigation = (steps: GenericApplicationStepValue[]) => {
    setSteps(steps);
    setStep(steps[0]);
  };

  const setStep = (step: GenericApplicationStepValue) => {
    updateApplicationContext({ step });
  };

  const setSteps = (steps: GenericApplicationStepValue[]) => {
    updateApplicationContext({ steps });
  };

  const updateStepsAndGoNext = (newSteps: GenericApplicationStepValue[]) => {
    if (!step) return;
    const nextStep = newSteps[newSteps.indexOf(step) + 1];
    updateApplicationContext({
      steps: newSteps,
      ...(nextStep && { step: nextStep }),
    });
  };

  const toggleShowMobileSummary = () => {
    updateApplicationContext({ showMobileSummary: !showMobileSummary });
  };

  const setDirty = () => {
    updateApplicationContext({ isDirty: true });
  };

  const setCompleted = () => {
    updateApplicationContext({ isCompleted: true });
  };

  const isFirstStep = step && steps && steps.indexOf(step) === 0;

  return {
    initApplication,
    resetApplicationContext,
    step,
    onPrev,
    onNext,
    resetNavigation,
    setSteps,
    updateStepsAndGoNext,
    showMobileSummary,
    toggleShowMobileSummary,
    isDirty,
    setDirty,
    isCompleted,
    setCompleted,
    isFirstStep,
  };
};
